import { Constructable } from "@spawn/common";
import { ButtonStateController, consumer, EventDispatcher, GameState, IGameStateOptions, inject } from "@spawn/core";
import {
	FreegameIdleState,
	FreegameOutroState,
	FreegameService,
	FreegameSpinningState,
	FreeGameStateHooks,
	FreegameWinPartyState,
} from "@spawn/freegames";
import { ResumeType } from "@spawn/networking";
import { NudgingService } from "@spawn/nudge";
import { RevealService, RevealState } from "@spawn/reveal";
import {
	BackgroundTexture,
	LaglessGenerator,
	NetworkService,
	showBackgroundEvent,
	SlotMachineButtonId,
	SlotMachineIdleState,
	WinPartyState,
} from "@spawn/slot-machine";
import { GameEvents } from "../events/events";
import { IGameNetworkState } from "../network/networkState";
import { ReelSetName } from "../config/reelSetName";
import { ReelsetService } from "../services/reelsetService";
import { FreegameInitialNudgingState } from "./freegameInitialNudgingState";
import { FreegameNudgeState } from "./freegameNudgeState";

@consumer
export class GameStateHooks {
	@inject private readonly eventDispatcher!: EventDispatcher;
	@inject private readonly networkService!: NetworkService;
	@inject private readonly revealService!: RevealService;
	@inject private readonly freegameService!: FreegameService;
	@inject private readonly nudgingService!: NudgingService;
	@inject private readonly laglessGenerator!: LaglessGenerator;
	@inject private readonly reelsetService!: ReelsetService;
	@inject private readonly buttonStateController!: ButtonStateController;

	constructor() {
		// Subscribe to freegame active state changes to automatically switch lagless configurations
		// eslint-disable-next-line @spawn/unmanaged-rxjs-subscription
		this.freegameService.isActive$.subscribe((isFreegameActive: boolean) => {
			const targetConfig = isFreegameActive ? ReelSetName.FreeGame : ReelSetName.BaseGame;
			this.laglessGenerator.activeConfig = targetConfig;
		});
	}

	public ResumeHooks(): IGameStateOptions {
		return {
			onComplete: async (nextState: Constructable<GameState>) => {
				// Goes directly to the gambling state If a risk game is resumed.
				if (this.networkService.state.session.resumeType === ResumeType.Risk) {
					return nextState;
				}

				const freegameState = this.freegameService.state;
				if (freegameState) {
					this.buttonStateController.updateState([SlotMachineButtonId.MaxBet], {
						isVisible: false,
						isEnabled: false,
					});

					if (freegameState.currentRound === 0) {
						return nextState;
					}

					this.prepareFreegameResume();

					if (freegameState.currentRound === 1) {
						await this.networkService.performAction("PSEUDO_INIT");
						return FreegameInitialNudgingState;
					} else {
						return FreegameSpinningState;
					}
				}

				return nextState;
			},
		};
	}

	private prepareFreegameResume(): void {
		this.freegameService.isActive = true;
		this.buttonStateController.setVisible([SlotMachineButtonId.BonusBuy], false);
		this.eventDispatcher.dispatch(showBackgroundEvent(BackgroundTexture.SideGame));
	}

	public SpinningHooks(): IGameStateOptions {
		return {
			onLeave: () => this.nudgingService.complete(),
			onComplete: () => RevealState,
		};
	}

	public WinPartyHooks(fgHooks: FreeGameStateHooks): IGameStateOptions {
		return {
			...fgHooks.WinPartyHook(),
		};
	}

	public FGWinpartyHooks(): IGameStateOptions {
		return {
			onComplete: () => {
				const { currentRound, totalRounds } = (this.networkService.state as IGameNetworkState).slotGameFreeGame!;
				const isLastSpin = currentRound === totalRounds;

				return isLastSpin ? FreegameOutroState : FreegameNudgeState;
			},
		};
	}

	public FGSpinningHooks(): IGameStateOptions {
		return {
			...this.SpinningHooks(),
			onEnter: () => {
				const offset = this.networkService.resuming ? 0 : 1;
				const multiplier = ((this.networkService.state as IGameNetworkState).slotGameFreeGame?.currentRound ?? 0) + offset;
				this.eventDispatcher.dispatch({
					type: GameEvents.SetMultiplier,
					payload: { multiplier },
				});
			},
		};
	}

	public GoldenBonusHooks(): IGameStateOptions {
		return {
			// Golden Bonus State only exists to BaseGame, so we need to redirect if we are in Freegame
			onComplete: (nextDefaultState: Constructable<GameState>) => {
				const isFGActive = this.freegameService.isActive;

				if (!isFGActive) {
					// Check for edge case where we win free games with only scatters (they don't come as paylines)
					const wonFreeGames = (this.networkService.state as IGameNetworkState).slotGameFreeGame?.wonFreegames;
					if (nextDefaultState.name === SlotMachineIdleState.name && wonFreeGames) {
						return WinPartyState;
					}
					return nextDefaultState;
				}

				const freegameState = this.freegameStateAfterGoldenBonus(nextDefaultState);
				if (freegameState) {
					return freegameState;
				}

				const nextStateMapping = {
					[WinPartyState.name]: FreegameWinPartyState,
					[SlotMachineIdleState.name]: FreegameIdleState,
				};

				return nextStateMapping[nextDefaultState.name] || nextDefaultState;
			},
		};
	}

	/**
	 * The free-game state after the golden bonus, before the default mapping applies.
	 * Return undefined to use the default mapping.
	 */
	protected freegameStateAfterGoldenBonus(_nextDefaultState: Constructable<GameState>): Constructable<GameState> | undefined {
		return undefined;
	}

	public FreegameOutroHooks(): IGameStateOptions {
		return {
			onLeave: () => {
				this.eventDispatcher.dispatch({ type: GameEvents.SetMultiplier, payload: { multiplier: 0 } });
				const triggerState = (this.networkService.state as IGameNetworkState).fgTriggerState;
				if (!triggerState?.reels?.length) {
					this.networkService.networkState.updateState((state: IGameNetworkState) => ({
						...state,
						fgTriggerState: undefined,
					}));
					return;
				}

				this.networkService.networkState.updateState((state: IGameNetworkState) => ({
					...state,
					slotGameBase: {
						...state.slotGameBase,
						reels: triggerState.reels,
					},
					nudging: triggerState.nudging,
					reveal: triggerState.reveal,
					fgTriggerState: undefined,
				}));
				this.reelsetService.replaceVisibleSymbolsOnReelset(triggerState.reels);
				this.revealService.uncoverInstant(triggerState.reveal?.symbols ?? []);
			},
		};
	}
}
